import { useEffect, useState, Fragment } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { zar, fmtDateTime } from "../lib/format";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

export default function PosShifts() {
  const [current, setCurrent] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    api.get("/pos/shifts/current").then((r) => setCurrent(r.data.id ? r.data : null)).catch(() => {});
    api.get("/pos/shifts").then((r) => setShifts(r.data)).catch(() => toast.error("Failed to load shifts"));
  };
  useEffect(() => { load(); }, []);

  const open = async () => {
    try {
      await api.post("/pos/shifts/open");
      toast.success("Shift opened");
      load();
    } catch (e) {
      toast.error(apiError(e, "Open failed"));
    }
  };

  const close = async () => {
    try {
      const { data } = await api.post(`/pos/shifts/${current.id}/close`);
      toast.success(`Shift closed — ${data.final_totals.receipts} receipts, sales ${zar(data.final_totals.sales)}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Close failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="pos-shifts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Shifts</h1>
          <p className="text-muted-foreground mt-1">Sales session boundaries for reconciliation (Z-report style). Receipts post in real time whether or not a shift is open.</p>
        </div>
        {current ? (
          <Button onClick={close} data-testid="shift-close-btn" className="bg-[#B33939] hover:bg-[#8f2d2d] text-white">Close shift</Button>
        ) : (
          <Button onClick={open} data-testid="shift-open-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">Open shift</Button>
        )}
      </div>

      {current && (
        <div className="bg-[#4A5D23]/5 border border-[#4A5D23]/30 rounded-lg px-5 py-4" data-testid="current-shift-card">
          <p className="font-heading font-semibold">Shift open</p>
          <p className="text-sm text-muted-foreground mt-0.5">Opened {fmtDateTime(current.opened_at)} by {current.opened_by}</p>
        </div>
      )}

      <div className="glass border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="shifts-table">
          <thead><tr className="border-b text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium"></th>
            <th className="px-4 py-3 font-medium">Opened</th><th className="px-4 py-3 font-medium">Closed</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium text-right">Receipts</th>
            <th className="px-4 py-3 font-medium text-right">Items</th>
            <th className="px-4 py-3 font-medium text-right">Sales</th>
            <th className="px-4 py-3 font-medium text-right">Tips</th>
          </tr></thead>
          <tbody>
            {shifts.map((s) => {
              const t = s.totals || s.final_totals || { receipts: 0, items: 0, sales: 0, tips: 0 };
              return (
                <Fragment key={s.id}>
                  <tr className="border-b last:border-0 hover:bg-accent/50 cursor-pointer" data-testid={`shift-row-${s.id}`}
                    onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                    <td className="px-4 py-2.5">{expanded === s.id ? <CaretDown size={14} /> : <CaretRight size={14} />}</td>
                    <td className="px-4 py-2.5">{fmtDateTime(s.opened_at)}<span className="block text-[10px] text-muted-foreground">{s.opened_by}</span></td>
                    <td className="px-4 py-2.5">{s.closed_at ? fmtDateTime(s.closed_at) : "—"}<span className="block text-[10px] text-muted-foreground">{s.closed_by || ""}</span></td>
                    <td className="px-4 py-2.5">
                      <Badge variant={s.status === "open" ? "default" : "outline"} className={s.status === "open" ? "bg-[#4A5D23] text-white" : ""}>{s.status}</Badge>
                    </td>
                    <td className="px-4 py-2.5 num text-right">{t.receipts}</td>
                    <td className="px-4 py-2.5 num text-right">{t.items}</td>
                    <td className="px-4 py-2.5 num text-right font-semibold">{zar(t.sales)}</td>
                    <td className="px-4 py-2.5 num text-right">{zar(t.tips)}</td>
                  </tr>
                  {expanded === s.id && (
                    <tr><td colSpan={8} className="px-4 pb-3 bg-accent/30">
                      {(s.receipt_list || []).length === 0 ? (
                        <p className="text-xs text-muted-foreground py-2">No receipts in this shift.</p>
                      ) : (
                        <table className="w-full text-xs" data-testid={`shift-receipts-${s.id}`}>
                          <tbody>
                            {s.receipt_list.map((r) => (
                              <tr key={r.id} className="border-b last:border-0 border-border/50" data-testid={`receipt-row-${r.id}`}>
                                <td className="py-1.5 font-medium">{r.receipt_no}</td>
                                <td className="py-1.5">{r.table_name}</td>
                                <td className="py-1.5">{r.closed_by}</td>
                                <td className="py-1.5 num text-right">{zar(r.subtotal)}</td>
                                <td className="py-1.5 num text-right">tip {zar(r.tip)}</td>
                                <td className="py-1.5 num text-right font-semibold">{zar(r.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td></tr>
                  )}
                </Fragment>
              );
            })}
            {shifts.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No shifts yet — open one before service to group receipts for reconciliation.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
